import { useState, useEffect, useRef } from 'react';
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';

interface NavbarProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

// Clear navigation component that handles scrolling to page sections
const Navbar = ({ isDarkMode, onToggleTheme }: NavbarProps) => {
  // State management for mobile menu, scroll position and active section
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('accueil');
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionsRef = useRef<Array<{ id: string; top: number; height: number }>>([]);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const measureSections = () => {
      sectionsRef.current = Array.from(document.querySelectorAll('section[id]')).map(section => {
        const element = section as HTMLElement;
        return {
          id: section.getAttribute('id') || 'accueil',
          top: element.offsetTop,
          height: element.offsetHeight,
        };
      });
    };

    const updateFromScroll = () => {
      const scrollY = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const nextProgress = documentHeight > 0 ? Math.min(scrollY / documentHeight, 1) : 0;

      setScrolled(prev => (prev !== (scrollY > 20) ? scrollY > 20 : prev));
      setScrollProgress(prev => (Math.abs(prev - nextProgress) > 0.002 ? nextProgress : prev));

      let nextActive = 'accueil';
      for (const section of sectionsRef.current) {
        const sectionTop = section.top - 100;
        if (scrollY > sectionTop && scrollY <= sectionTop + section.height) {
          nextActive = section.id;
          break;
        }
      }
      setActiveSection(prev => (prev !== nextActive ? nextActive : prev));

      rafRef.current = null;
    };

    const handleScroll = () => {
      if (rafRef.current === null) {
        rafRef.current = window.requestAnimationFrame(updateFromScroll);
      }
    };

    measureSections();
    updateFromScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', measureSections);
    window.addEventListener('orientationchange', measureSections);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', measureSections);
      window.removeEventListener('orientationchange', measureSections);
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Smooth scroll to section and close mobile menu
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  const navLinks = [
    { id: 'accueil', label: 'Accueil' },
    { id: 'mission', label: 'Nos actions' },
    { id: 'actions', label: 'Actualités' },
    { id: 'calendrier', label: 'Calendrier' },
    { id: 'benevole', label: 'Bénévole' },
    { id: 'partenaires', label: 'Partenaires' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <nav className={`fixed w-full z-50 bg-paper/95 backdrop-blur-md border-b border-ink/10 transition-shadow duration-300 ${scrolled ? 'shadow-sm' : ''}`}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <button type="button" className="flex items-center group shrink-0" onClick={() => scrollToSection('accueil')} aria-label="Retour à l'accueil">
            <img
              src="/images/nousrire_logo.svg"
              alt="Nous'Rire Logo"
              className="h-11 w-auto"
            />
            <span className="ml-3 text-xl font-extrabold tracking-tight text-ink hidden sm:inline lg:hidden xl:inline">
              Nous'Rire
            </span>
          </button>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex flex-grow justify-center items-center">
            <div className="flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`relative py-1.5 px-2.5 text-sm font-semibold whitespace-nowrap rounded-full transition-colors duration-200
                    ${activeSection === link.id
                      ? 'text-accent'
                      : 'text-ink/70 hover:text-ink'
                    }
                  `}
                  aria-current={activeSection === link.id ? 'true' : undefined}
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-3 ml-4">
            <button onClick={() => scrollToSection('don')} className="btn-primary px-5 py-2.5 text-sm">
              Faire un don
            </button>
            <button
              onClick={onToggleTheme}
              className="p-2 rounded-full border border-ink/15 hover:bg-ink/5 transition-colors"
              aria-label={isDarkMode ? 'Activer le thème clair' : 'Activer le thème sombre'}
              title={isDarkMode ? 'Thème clair' : 'Thème sombre'}
            >
              {isDarkMode ? (
                <SunIcon className="h-5 w-5 text-ink" />
              ) : (
                <MoonIcon className="h-5 w-5 text-ink" />
              )}
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-full border border-ink/15 text-ink"
              aria-label={isOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={isOpen}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`lg:hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-screen opacity-100 py-6' : 'max-h-0 opacity-0'
            } overflow-hidden bg-paper border-t border-ink/10 mt-2`}
        >
          <div className="px-6 space-y-4">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`block w-full text-left text-lg font-semibold transition-all
                  ${activeSection === link.id
                    ? 'text-accent pl-4 border-l-4 border-accent'
                    : 'text-ink/70 hover:text-ink hover:pl-2'
                  }
                `}
              >
                {link.label}
              </button>
            ))}
            <button onClick={() => scrollToSection('don')} className="btn-primary w-full">
              Faire un don
            </button>
            <button
              onClick={onToggleTheme}
              className="flex w-full items-center gap-3 py-4 border-t border-ink/10 text-ink/70 font-semibold"
            >
              <div className="p-2 rounded-full bg-brand-pink-50 text-accent">
                {isDarkMode ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
              </div>
              {isDarkMode ? 'Thème clair' : 'Thème sombre'}
            </button>
          </div>
        </div>
      </div>
      <div className="h-[2px] w-full bg-transparent origin-left overflow-hidden">
        <div
          className="h-full bg-brand"
          style={{ transform: `translateZ(0) scaleX(${scrollProgress})`, transformOrigin: 'left', willChange: 'transform' }}
        />
      </div>
    </nav>
  );
};

export default Navbar;